import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { dequal } from "dequal";
import { DateTime } from "luxon";
import type { FilterSelection } from "@interfaces/filters/domain";
import type { QuestionPickOption } from "@utils/viewDefinition/types";

export interface UserSelectionState {
  base: string;
  statTest: string;
  /**
   * The main analysis window. In PRE_POST this is the POST period; in every
   * other mode it is the single active period. Named to match the saved-view
   * definition contract (`selection.period`) and the URL, so an unprefixed
   * `period*` means the same thing everywhere.
   */
  periodType: string;
  periodStartDate: string;
  periodEndDate: string;
  /** TREND interval type (MV_12, P_M, …) — unrelated to the windows here. */
  period: string;
  /**
   * The PRE window. Only meaningful in PRE_POST mode; ignored elsewhere.
   * Mirrors `selection.prePeriod` in the definition contract.
   */
  prePeriodType: string;
  prePeriodStartDate: string;
  prePeriodEndDate: string;
  mode:
    | "POINT_IN_TIME"
    | "TREND"
    | "PRE_POST"
    | "BUTTERFLY"
    | "DISTRIBUTION"
    | "PERCEPTUAL"
    | "DIFFERENTIATION";
  brands: (number | string)[]; // Multiple brands as array (string for brand sets)
  secondaryBrands: (number | string)[];
  markets: number[]; // Multiple markets as array
  segments: string[]; // Only plural key, no singular key; ULID codes
  filters: FilterSelection[];
  groupedBy: string;
  /**
   * Exclusive-questions picks per question key. Each pick carries the `type`
   * that was stamped at the source (picker click, typed URL param, or the
   * RESPONSE default for legacy `questionOptions=KEY-id` links). Types are
   * only ever FILTERED, never recomputed: the three id sequences (responses,
   * system rollups, user rollups) collide, so catalog membership cannot
   * resolve a pick's type.
   */
  questionOptions: Record<string, QuestionPickOption[]>;
  visualizationType: string;
  /**
   * Calculation method (PERCENTAGE / MEAN / LEGACY …). Empty string means
   * "unresolved" — never a method: the initial state ships `""` deliberately so
   * that the FIRST prune cycle resolves the entered module's catalog default,
   * exactly as an in-app module change does. Seeding a concrete method here
   * instead would make cold entry (hard refresh / Home redirect / saved-view
   * tab) silently keep that seed, because the module is already committed by
   * `useURLToStore` before the bootstrap pending dispatch and so reads as
   * "unchanged" to the module-change reset.
   *
   * `buildURLFromSelection` skips empty values, so `""` never reaches the URL.
   */
  scoreType: string;
  /**
   * Semantic response key (two-axis catalog "response" slice within the
   * resolved `scoreType`). Non-null iff the current method carries
   * responses in context (e.g. PERCENTAGE has responses, MEAN does not).
   * Populated/pruned by the selection listener against the metrics catalog.
   */
  response: string | null;
  profileMetric: string | null;  // distribution categoryKey (was legacy numeric metric id)
  profileGroup: string | null;   // audienceBase key (was legacy numeric group id)
  profileResponse: string | null; // distribution familyKey (was legacy numeric response id)
  trendsMetric: string | null;
  /** Selected AI category id. Null when the category control is unused. */
  category: number | null;
  /** Selected human comparison metric keys (awareness, consideration, …). */
  humanMetrics: string[];
  /**
   * Selected AI assistant (ChatGPT/Gemini/Claude) integer id(s) for AI
   * Visibility's By Model screens and By Brand's model filter. PRE_POST
   * mode holds exactly one id (the breakdown would otherwise combine every
   * assistant's every metric into one cluttered chart — narrows the view to
   * a single assistant's full metric set instead); POINT_IN_TIME's
   * multi-select (`config.aiModelIsMultiSelect`) and By Brand's optional
   * filter both hold every available id to mean "All Models" — the selection
   * listener resolves an empty/invalid selection to the full id list rather
   * than leaving it empty, so a specific single pick (length 1) is always
   * an intentional narrowing, never the default.
   */
  models: number[];
  viewId: string | null;
  hiddenSegments?: number[];
  hiddenBrands?: number[];
  hiddenModels?: number[];
  screenType: string;
  hiddenMarkets?: number[];
  hiddenQuestions?: string[];
  module: string;
  reportId: string | null;
  /**
   * A fingerprint of the currently-selected brand set's member composition.
   *
   * Computed automatically by `useModeData` from the brand entity data in
   * `brandsSlice` — NOT written via `syncFromURL` or any explicit dispatch.
   *
   * When a brand set is edited (`updateBrand` dispatched), the entity's
   * `members` array changes.  `useModeData`'s selector re-computes this
   * fingerprint, `apiTriggeringSelection` in `useModeViewData` changes
   * (because `"brandSetVersion"` is in `API_TRIGGERING_KEYS`), and Effect 1
   * re-runs — calling the unified analytics API with the updated composition.
   *
   * This field is NOT serialised to the URL and is never written to Redux
   * directly; it exists only as a type anchor for the `API_TRIGGERING_KEYS`
   * set and the `useModeData` selector.
   */
  brandSetVersion?: string | number;
}

export const initialState: UserSelectionState = {
  module: "visibility",
  reportId: null,
  base: "GENPOP",
  statTest: "POP",
  periodType: "L180D",
  screenType: "",
  mode: "POINT_IN_TIME",
  periodStartDate: DateTime.now().minus({ days: 180 }).toFormat("yyyy-MM-dd"),
  periodEndDate: DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd"),
  period: "MV_12",
  prePeriodType: "L180D",
  prePeriodStartDate: DateTime.now().minus({ days: 180 }).toFormat("yyyy-MM-dd"),
  prePeriodEndDate: DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd"),
  visualizationType: "",
  groupedBy: "RESPONSE",
  brands: [], // Multiple brands as array
  secondaryBrands: [],
  markets: [], // Multiple markets as array
  segments: [], // Only plural key, no singular key
  filters: [],
  questionOptions: {},
  // "" = unresolved; the first prune snaps it to the entered module's default.
  scoreType: "",
  response: null,
  profileGroup: null,
  profileMetric: null,
  profileResponse: null,
  trendsMetric: null,
  category: null,
  humanMetrics: [],
  models: [],
  viewId: null,
  hiddenSegments: [],
  hiddenBrands: [],
  hiddenModels: [],
  hiddenMarkets: [],
};

interface UpdateFieldPayload {
  field: keyof UserSelectionState;
  // The transitional `filters` field carries the FilterSelection[] shape ahead
  // of the state type change landing (see selectDraftReadyFilterSelections).
  value: string | string[] | FilterSelection[];
}

export const userSelectionSlice = createSlice({
  name: "userSelection",
  initialState,
  reducers: {
    updateSelection: (
      state,
      action: PayloadAction<Partial<UserSelectionState>>
    ) => {
      // Update multiple fields at once
      return { ...state, ...action.payload };
    },
    updateField: (state, action: PayloadAction<UpdateFieldPayload>) => {
      // Update a single field
      const { field, value } = action.payload;
      (state as any)[field] = value;
    },
    resetSelection: () => {
      return initialState;
    },
    syncFromURL: (
      state: UserSelectionState,
      action: PayloadAction<Partial<UserSelectionState>>
    ) => {
      Object.entries(action.payload).forEach(([key, value]: [string, any]) => {
        //@ts-ignore
        if (!dequal(state[key], value)) {
          //@ts-ignore
          state[key] = value;
        }
      });
      return state;
    },
  },
});

export const { updateSelection, updateField, resetSelection, syncFromURL } =
  userSelectionSlice.actions;

export default userSelectionSlice.reducer;
